import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useAppDispatch, useAppSelector } from '../store';
import { functions } from '../config/firebase';

import { fetchBrokers } from '../store/slices/brokerSlice';

import { httpsCallable } from 'firebase/functions';

const convertToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64String = (reader.result as string).split(',')[1];
      resolve(base64String);
    };
    reader.onerror = error => reject(error);
  });
};

const DriverPortal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user, userInfo } = useAppSelector((state) => state.auth);
  const { items: brokers, loading: brokersLoading } = useAppSelector((state) => state.brokers);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [selectedBroker, setSelectedBroker] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string>('');

  useEffect(() => {
    dispatch(fetchBrokers());
  }, [dispatch]);

  const onDrop = (acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setError(null);
    } else {
      setError('Please upload a PDF file');
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    multiple: false
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    if (!file) {
      setError("Please upload a PDF file");
      setLoading(false);
      return;
    }

    if (!invoiceNumber) {
      setError("Please enter an invoice number");
      setLoading(false);
      return;
    }

    if (!selectedBroker) {
      setError("Please select a broker");
      setLoading(false);
      return;
    }

    try {
      console.log("Processing file...");
      // Convert file to base64
      const base64File = await convertToBase64(file);
      console.log("File converted to base64");

      // Get broker object and email
      const brokerObj = brokers.find(b => b.id === selectedBroker);
      const brokerEmail = brokerObj?.email || '';
      console.log("Selected broker email:", brokerEmail);

      // Prepare email data
      const emailData = {
        to: brokerEmail,
        subject: `Invoice ${invoiceNumber} from ${user?.displayName || 'Driver'}`,
        text: `Invoice Number: ${invoiceNumber}\nDriver: ${user?.displayName || 'N/A'}\nDogtag: ${user?.dogtag || 'N/A'}\n\nAdditional Notes:\n${description}`,
        attachment: {
          filename: file.name,
          content: base64File,
          contentType: 'application/pdf'
        }
      };

      console.log("Calling Cloud Function...");
      const sendEmail = httpsCallable(functions, 'sendEmail');
      const result = await sendEmail(emailData);
      console.log("Cloud Function response:", result);

      const response = result.data as { success: boolean; messageId?: string; error?: { code: string; message: string; details?: string } };

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to send email');
      }

      setSuccess(`Email sent successfully! Message ID: ${response.messageId}`);
      setFile(null);
      setInvoiceNumber('');
      setDescription('');
      setSelectedBroker('');
    } catch (error: any) {
      console.error("Detailed error:", error);
      console.error("Error stack:", error.stack);
      
      let errorMessage = 'Failed to send email';
      try {
        if (error.message) {
          errorMessage = error.message;
        }
      } catch (parseError) {
        console.error("Error parsing error message:", parseError);
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography 
          component="h1" 
          variant="h4" 
          sx={{ 
            mb: 4,
            fontWeight: 600,
            color: 'primary.main',
          }}
        >
          Upload Invoice
        </Typography>

        <Box 
          component="form" 
          onSubmit={handleSubmit}
          sx={{ 
            width: '100%',
            maxWidth: 600,
          }}
        >
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              mb: 3,
              backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <input {...getInputProps()} disabled={loading} />
            {file ? (
              <Typography>
                Selected file: {file.name}
              </Typography>
            ) : (
              <Typography>
                {isDragActive
                  ? 'Drop the PDF here'
                  : 'Drag and drop a PDF file here, or click to select'}
              </Typography>
            )}
          </Box>

          <FormControl fullWidth margin="normal" required>
            <InputLabel>Broker</InputLabel>
            <Select
              value={selectedBroker}
              label="Broker"
              onChange={(e) => setSelectedBroker(e.target.value)}
              disabled={loading || brokersLoading}
            >
              {brokers.map((broker) => (
                <MenuItem key={broker.id} value={broker.id}>
                  {broker.shortname}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Invoice Number"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            label="Description/Summary"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            multiline
            rows={8}
            placeholder="Additional Notes?"
            disabled={loading}
            sx={{ 
              mb: 3,
              '& .MuiInputBase-root': {
                height: '200px'
              }
            }}
          />

          {uploadProgress && (
            <Alert 
              severity="info" 
              sx={{ mb: 2 }}
            >
              {uploadProgress}
            </Alert>
          )}

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ mb: 2 }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || !file || !invoiceNumber.trim() || !selectedBroker}
            sx={{ 
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Upload Invoice'
            )}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default DriverPortal; 